import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { euro, euroShort } from "../../lib/format";
import type { Projection } from "../../lib/scenario";

/**
 * Recorded value on the left of the base year, scenario on the right.
 *
 * The two are drawn differently on purpose — solid for what happened, dashed
 * inside a band for what is being imagined. The band is a spread of
 * assumptions, not a confidence interval, and the caption below says so.
 */
export function ProjectionChart({ projection }: { projection: Projection }) {
  const { rows, base } = projection;

  const values = rows.flatMap((r) => [
    r.actual,
    r.projected,
    ...(r.band ?? []),
    ...(r.actualRange ?? []),
  ]);
  const known = values.filter((v): v is number => v !== null);
  const lo = Math.min(...known);
  const hi = Math.max(...known);
  const pad = Math.max((hi - lo) * 0.08, 5000);

  return (
    <div className="h-40 w-full">
      <ResponsiveContainer>
        <ComposedChart data={rows} margin={{ top: 6, right: 6, bottom: 0, left: -6 }}>
          <XAxis
            dataKey="year"
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={18}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            width={46}
            domain={[Math.floor(lo - pad), Math.ceil(hi + pad)]}
            tickFormatter={euroShort}
          />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e3e7ed" }}
            labelFormatter={(y) => `${y}`}
            formatter={(value, name, item) => {
              const r = item.payload as (typeof rows)[number];
              if (name === "actual") return [euro(r.actual ?? 0), "recorded"];
              if (name === "projected") return [euro(r.projected ?? 0), "scenario"];
              if (name === "band" && r.band)
                return [`${euroShort(r.band[0])} – ${euroShort(r.band[1])}`, "range"];
              return [String(value), String(name)];
            }}
          />
          <Area
            dataKey="band"
            stroke="none"
            fill="#2f6df6"
            fillOpacity={0.12}
            isAnimationActive={false}
            activeDot={false}
          />
          <ReferenceLine
            x={base.year}
            stroke="#5a6472"
            strokeDasharray="2 3"
            strokeOpacity={0.5}
          />
          <Line
            dataKey="actual"
            stroke="#14181f"
            strokeWidth={2}
            dot={{ r: 2 }}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Line
            dataKey="projected"
            stroke="#2f6df6"
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
